/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import {
    Paper,
    Stack,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Divider,
    Tooltip,
} from '@mui/material';
import { housingFilterOptions } from 'features/lab2/constants/filterOptions';
import { PlayArrow, Refresh } from '@mui/icons-material';

const themeColor = '#002755';

const HousingControlPanel = ({
    currentFilterValue,
    onFilterChange,
    areaRange,
    onAreaRangeChange,
    housingTypes,
    onHousingTypeChange,
    onSearch,
    onReset,
}) => {
    const handleSliderChange = (_, value) => {
        if(value[0] !== value[1]) {
            onAreaRangeChange?.(value);
        }
    };

    const areaStep = [
        { value: 15, label: 15 }, { value: 20, label: 20 }, { value: 25, label: 25 }, { value: 30, label: 30 }, { value: 35, label: 35 }, { value: 40, label: 40 }, 
        { value: 50, label: 50 }, { value: 60, label: 60 }, { value: 70, label: 70 }, { value: 80, label: 80 }, { value: 90, label: 90 }, { value: 100, label: 100 }, 
        { value: 125, label: 125 }, { value: 150, label: 150 }
    ]

    const CustomValueLabel = (props) => {
        const { children, value, open } = props;

        if (!open) {
            return children;
        }

        if (value === 200) {
            return children;
        }

        return (
            <Tooltip placement="top" title={value}>
                {children}
            </Tooltip>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e3e7f0',
                background: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}
        >
            <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                    지표 선택
                </Typography>
                <FormControl size="small" fullWidth>
                    <InputLabel>지표</InputLabel>
                    <Select
                        label="지표"
                        value={currentFilterValue || housingFilterOptions[0].value}
                        onChange={(e) => onFilterChange?.(e.target.value)}
                    >
                        {housingFilterOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Divider />

            <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor }}>
                    주택 정보 검색
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    면적 범위와 주택 유형을 선택하고 검색을 실행하세요.
                </Typography>
            </Stack>

            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        면적 범위 (㎡)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {`${areaRange[0]}㎡ ~ ${areaRange[1] === 200 ? '' : `${areaRange[1]}㎡`}`}
                    </Typography>
                </Stack>
                <Slider
                    value={areaRange}
                    onChange={handleSliderChange}
                    valueLabelDisplay='auto'
                    slots={{ valueLabel: CustomValueLabel}}
                    sx={{
                        color: themeColor,
                        '& .MuiSlider-markLabel': {
                            display: 'none',
                        },
                    }}
                    min={Math.min(...areaStep.map((step) => step.value))}
                    max={Math.max(...areaStep.map((step) => step.value))}
                    step={null}
                    marks={areaStep}
                    disableSwap
                />
            </Stack>

            <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    주택 유형
                </Typography>
                <FormGroup>
                    {['apartment', 'multiFamily', 'officetel'].map((key) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={Boolean(housingTypes?.[key])}
                                    onChange={(e) => onHousingTypeChange?.(key, e.target.checked)}
                                />
                            }
                            label={
                                key === 'apartment'
                                    ? '아파트'
                                    : key === 'multiFamily'
                                    ? '다세대 / 연립'
                                    : '오피스텔'
                            }
                        />
                    ))}
                </FormGroup>
            </Stack>

            {/* <Stack direction="row" spacing={1.5}>
                <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={onSearch}
                    sx={{ flex: 1, bgcolor: themeColor, '&:hover': { bgcolor: '#001b3d' }, fontWeight: 700 }}
                >
                    검색
                </Button>
                <Button variant="outlined" startIcon={<Refresh />} onClick={onReset} sx={{ flex: 1 }}>
                    초기화
                </Button>
            </Stack> */}
        </Paper>
    );
};

HousingControlPanel.propTypes = {
    currentFilterValue: PropTypes.string,
    onFilterChange: PropTypes.func,
    areaRange: PropTypes.arrayOf(PropTypes.number).isRequired,
    onAreaRangeChange: PropTypes.func.isRequired,
    housingTypes: PropTypes.shape({
        apartment: PropTypes.bool,
        multiFamily: PropTypes.bool,
        officetel: PropTypes.bool,
    }).isRequired,
    onHousingTypeChange: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
};

export default HousingControlPanel;
